import type {
  IssueFieldSelectionValues,
  IssueFieldSingleSelectOption,
  IssueFieldType,
} from '@github-ui/issue-fields-shared/issue-field'
import {
  ISSUE_FIELD_DATE_VALUE_TYPE,
  ISSUE_FIELD_MULTI_SELECT_VALUE_TYPE,
  ISSUE_FIELD_NUMBER_VALUE_TYPE,
  ISSUE_FIELD_SINGLE_SELECT_VALUE_TYPE,
  ISSUE_FIELD_TEXT_VALUE_TYPE,
} from '@github-ui/issue-fields-shared/issue-field'
import type {EntityOperation} from '@github-ui/optimistic-connection'
import type {RecordProxy, RecordSourceSelectorProxy} from 'relay-runtime'

import type {IssueFieldCreateOrUpdateInput} from './__generated__/updateIssueFieldValueMutation.graphql'

export type OptimisticIssueFieldValue = {
  __typename:
    | 'IssueFieldTextValue'
    | 'IssueFieldSingleSelectValue'
    | 'IssueFieldMultiSelectValue'
    | 'IssueFieldDateValue'
    | 'IssueFieldNumberValue'
  field: {id: string; name: string}
  value?: string | null
  name?: string | null
  options?: Array<{name: string}>
  numberValue?: number | null
}

export function upsertIssueFieldValue(issueId: string, value: OptimisticIssueFieldValue): EntityOperation {
  return {
    type: 'upsertEntityFieldItem',
    id: issueId,
    field: 'issueFieldValues',
    key: value.field.id,
    item: value,
  }
}

export function removeIssueFieldValue(issueId: string, fieldId: string, fieldName: string): EntityOperation {
  return {
    type: 'removeEntityFieldItem',
    id: issueId,
    field: 'issueFieldValues',
    key: fieldId,
    item: {field: {id: fieldId, name: fieldName}},
  }
}

export function toOptimisticIssueFieldValue(
  fieldId: string,
  fieldName: string,
  fieldType: IssueFieldType,
  selection: IssueFieldSelectionValues,
): OptimisticIssueFieldValue | undefined {
  const field = {id: fieldId, name: fieldName}
  switch (fieldType) {
    case 'text':
      return typeof selection === 'string' ? {__typename: 'IssueFieldTextValue', field, value: selection} : undefined
    case 'single_select': {
      const option = selection as IssueFieldSingleSelectOption | null
      return option && typeof option.name === 'string'
        ? {__typename: 'IssueFieldSingleSelectValue', field, name: option.name}
        : undefined
    }
    case 'multi_select': {
      if (!Array.isArray(selection) || !selection.every(option => typeof option.name === 'string')) return undefined
      return {
        __typename: 'IssueFieldMultiSelectValue',
        field,
        options: selection.map(option => ({name: option.name})),
      }
    }
    case 'date':
      return selection instanceof Date
        ? {__typename: 'IssueFieldDateValue', field, value: selection.toISOString()}
        : undefined
    case 'number':
      return typeof selection === 'number'
        ? {__typename: 'IssueFieldNumberValue', field, numberValue: selection}
        : undefined
  }
}

export function normalizeIssueFieldValue(
  value: unknown,
  optimisticValue: Pick<OptimisticIssueFieldValue, '__typename' | 'field'>,
): OptimisticIssueFieldValue | undefined {
  if (!value || typeof value !== 'object') return undefined
  const record = value as Record<string, unknown>
  const {__typename, field} = optimisticValue

  switch (__typename) {
    case 'IssueFieldTextValue':
      return typeof record.value === 'string' || record.value === null
        ? {__typename, field, value: record.value}
        : undefined
    case 'IssueFieldSingleSelectValue':
      return typeof record.name === 'string' || record.name === null
        ? {__typename, field, name: record.name}
        : undefined
    case 'IssueFieldMultiSelectValue': {
      if (!Array.isArray(record.options)) return undefined
      const names = record.options.map(option =>
        option && typeof option === 'object' ? (option as Record<string, unknown>).name : undefined,
      )
      return names.every((name): name is string => typeof name === 'string')
        ? {
            __typename,
            field,
            options: names.map(name => ({name})),
          }
        : undefined
    }
    case 'IssueFieldDateValue':
      return typeof record.value === 'string' || record.value === null
        ? {__typename, field, value: record.value}
        : undefined
    case 'IssueFieldNumberValue':
      return typeof record.numberValue === 'number' || record.numberValue === null
        ? {__typename, field, numberValue: record.numberValue}
        : undefined
  }
}

// Helper to get field value type based on input
export const getFieldValueType = (
  fieldInput: Pick<
    IssueFieldCreateOrUpdateInput,
    'singleSelectOptionId' | 'multiSelectOptionIds' | 'dateValue' | 'numberValue'
  >,
) => {
  if (fieldInput.singleSelectOptionId !== undefined) return ISSUE_FIELD_SINGLE_SELECT_VALUE_TYPE
  if (fieldInput.multiSelectOptionIds !== undefined) return ISSUE_FIELD_MULTI_SELECT_VALUE_TYPE
  if (fieldInput.dateValue !== undefined) return ISSUE_FIELD_DATE_VALUE_TYPE
  if (fieldInput.numberValue !== undefined) return ISSUE_FIELD_NUMBER_VALUE_TYPE
  return ISSUE_FIELD_TEXT_VALUE_TYPE
}

// Helper to update node values
export const updateNodeValues = (
  store: RecordSourceSelectorProxy,
  node: ReturnType<RecordSourceSelectorProxy['get']> | null,
  fieldInput: IssueFieldCreateOrUpdateInput,
) => {
  if (!node) return
  if (fieldInput.textValue !== undefined) node.setValue(fieldInput.textValue, 'value')
  if (fieldInput.singleSelectOptionId !== undefined && fieldInput.singleSelectOptionId !== null) {
    const options = store.get(fieldInput.fieldId)?.getLinkedRecords('options') || []
    const found = options.find(opt => opt.getDataID() === fieldInput.singleSelectOptionId)
    node.setValue(found?.getValue('name') ?? null, 'name')
    node.setValue(found?.getValue('color') ?? null, 'color')
    node.setValue(found?.getValue('description') ?? null, 'description')
  }
  if (fieldInput.multiSelectOptionIds !== undefined && fieldInput.multiSelectOptionIds !== null) {
    const fieldOptions = store.get(fieldInput.fieldId)?.getLinkedRecords('options') || []
    const selectedIds = new Set(fieldInput.multiSelectOptionIds)
    const selectedOptions = fieldOptions.filter(opt => opt && selectedIds.has(opt.getDataID()))
    node.setLinkedRecords(selectedOptions, 'options')
  }
  if (fieldInput.dateValue !== undefined) node.setValue(fieldInput.dateValue, 'value')
  if (fieldInput.numberValue !== undefined) node.setValue(fieldInput.numberValue, 'value')
}

// Helper to replace or add a field value node in the collection
export const replaceFieldValueInCollection = (
  fieldValues: ReturnType<RecordSourceSelectorProxy['get']>,
  fieldId: string,
  newNode: NonNullable<ReturnType<RecordSourceSelectorProxy['get']>>,
) => {
  if (!fieldValues || !newNode) return
  const nodes = fieldValues.getLinkedRecords('nodes') || []
  const updatedNodes = nodes.filter(n => n?.getLinkedRecord('field')?.getDataID() !== fieldId)
  updatedNodes.push(newNode as RecordProxy)
  fieldValues.setLinkedRecords(updatedNodes, 'nodes')
}
